import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  CmsClient, activeTabId, applyPersisted, buildReminders, defaultAppConfig, enabledTabs,
  headerFor, headerOverride, initialState, launchThemeId, nocturneBundle, reducer, resolveBundle,
  sanitizeConfig, showsGridToggle, tabActions, toPersisted, unreadCount,
  type AppConfig, type PersistedState, type TabDef, type TenantBundle,
} from '@ce/core';
import { ThemeProvider } from './ThemeProvider';
import { Splash } from './components/Splash';
import { Drawer, Header, TabBar, TopTabs } from './components/Chrome';
import { BookingSheet } from './components/BookingSheet';
import { useHistoryRouting } from './useHistoryRouting';
import { PREVIEW, usePreviewBridge } from './preview';
import { Onboarding } from './screens/Onboarding';
import { CustomPage, Home } from './screens/Home';
import { Schedule, Timeline } from './screens/Schedule';
import { Talent, TalentDetail } from './screens/Talent';
import { Events, Hall } from './screens/Hall';
import { Quests } from './screens/Quests';
import { Me, Cart } from './screens/Me';
import { Search } from './screens/Search';
import { Journey } from './screens/Journey';
import { Messages, Shop, Tickets } from './screens/Commerce';
import { SessionDetail } from './screens/SessionDetail';

const CMS_BASE = import.meta.env.VITE_CMS_URL ?? '';
const TENANT = import.meta.env.VITE_TENANT_ID ?? 'nocturne-con';
const PERSIST_KEY = `ce.state.${TENANT}`;

const webStorage = {
  async getItem(key: string) {
    try { return window.localStorage.getItem(key); } catch { return null; }
  },
  async setItem(key: string, value: string) {
    try { window.localStorage.setItem(key, value); } catch { /* private mode */ }
  },
};

export default function App() {
  // Start from bundled defaults so the first paint has a theme and a layout; the
  // CMS results replace them as they land. Content and layout load independently:
  // a content outage leaves the layout alone, and a layout publish does not
  // refetch the programme.
  const [rawBundle, setRawBundle] = useState<TenantBundle>(nocturneBundle);
  const [config, setConfig] = useState<AppConfig>(() => defaultAppConfig(TENANT));
  const [loaded, setLoaded] = useState(!CMS_BASE);
  const [configReady, setConfigReady] = useState(PREVIEW);
  // In the admin preview there is nothing to wait for and no splash to sit through.
  const [splashDone, setSplashDone] = useState(PREVIEW);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  const [state, dispatch] = useReducer(reducer, rawBundle, (b) => {
    let persisted: PersistedState | null = null;
    if (!PREVIEW) {
      try {
        const raw = window.localStorage.getItem(PERSIST_KEY);
        persisted = raw ? (JSON.parse(raw) as PersistedState) : null;
      } catch { /* ignore */ }
    }
    const restored = applyPersisted(initialState(b), persisted);
    // Decide the launch look once, during init, so the first paint is already
    // the right theme — resolving it in an effect would flash the default.
    // The editor previews the look it is configuring, not a random pick.
    const base = { ...restored, themeId: PREVIEW ? null : launchThemeId(b, persisted?.themeId ?? null, restored.themeShuffle) };
    // ...and the app, not its onboarding.
    return PREVIEW ? { ...base, screen: 'home' as const } : base;
  });

  // Theme resolves from content, config and the attendee's chosen variant, so
  // picking a look repaints without refetching or remounting anything.
  const { bundle, theme } = useMemo(
    () => resolveBundle(rawBundle, state.themeId, config),
    [rawBundle, state.themeId, config],
  );

  const client = useMemo(
    () => (CMS_BASE ? new CmsClient({ baseUrl: CMS_BASE, tenantId: TENANT, storage: webStorage, fallback: nocturneBundle }) : null),
    [],
  );

  // Content: cache-first, revalidating in the background.
  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    client
      .load((fresh) => { if (!cancelled) setRawBundle(fresh.bundle); })
      .then((first) => { if (!cancelled) { setRawBundle(first.bundle); setLoaded(true); } })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [client]);

  // Layout: the same strategy, from the same CMS — or, with no CMS, from a
  // config.json published beside the app (which is how a static host such as
  // GitHub Pages carries a configuration). Nothing at all means the default
  // layout, which is the app as it was before the admin existed.
  useEffect(() => {
    if (PREVIEW) return;
    let cancelled = false;
    const done = () => { if (!cancelled) setConfigReady(true); };
    // The splash must not wait forever on a slow network.
    const cap = window.setTimeout(done, 2500);

    if (client) {
      client
        .loadConfig((fresh) => { if (!cancelled) setConfig(fresh); })
        .then((c) => { if (!cancelled) setConfig(c); })
        .catch(() => { /* default stays */ })
        .finally(done);
    } else {
      fetch(`${import.meta.env.BASE_URL}config.json`, { cache: 'no-cache' })
        .then((r) => (r.ok ? r.json() : null))
        .then((json) => { if (!cancelled && json) setConfig(sanitizeConfig(json, TENANT).config); })
        .catch(() => { /* no file is normal */ })
        .finally(done);
    }
    return () => { cancelled = true; window.clearTimeout(cap); };
  }, [client]);

  const { highlightId, mode } = usePreviewBridge({ tenantId: TENANT, dispatch, onConfig: setConfig });

  // Persist the attendee's own data. Content is never persisted here — that is
  // the CMS cache's job. Skipped in preview so the editor cannot overwrite a
  // real attendee's state in the same browser.
  useEffect(() => {
    if (PREVIEW) return;
    try {
      window.localStorage.setItem(PERSIST_KEY, JSON.stringify(toPersisted(state)));
    } catch { /* ignore */ }
  }, [state]);

  // Local reminders for starred sessions, via the Notifications API when granted.
  useEffect(() => {
    if (PREVIEW || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const reminders = buildReminders(bundle, state.saved);
    const timers = reminders
      .filter((r) => r.fireAt - Date.now() < 24 * 60 * 60 * 1000)
      .map((r) =>
        window.setTimeout(
          () => new Notification(r.title, { body: r.body, tag: r.id }),
          Math.max(0, r.fireAt - Date.now()),
        ),
      );
    return () => timers.forEach(clearTimeout);
  }, [bundle, state.saved]);

  useHistoryRouting(state, dispatch, !PREVIEW);

  // A new screen starts at the top.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [state.screen, state.pageId, state.talentId, state.sessionId]);

  const header = useMemo(
    () => headerOverride(config, bundle, state, headerFor(bundle, state)),
    [config, bundle, state],
  );
  const onSplashDone = useCallback(() => setSplashDone(true), []);

  const tabs = enabledTabs(config);
  const activeId = activeTabId(config, state);
  const navStyle = config.navigation.style;
  const hdr = config.navigation.header;

  const selectTab = (tab: TabDef) => {
    tabActions(tab.target).forEach(dispatch);
    setDrawerOpen(false);
  };
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const screenProps = { bundle, state, dispatch, config, cmsUrl: CMS_BASE, highlightId };

  return (
    <ThemeProvider theme={theme}>
      <div className="ce-stage">
        <div
          className="ce-device"
          data-fx-ambient={config.effects.ambient ? 'on' : 'off'}
          data-fx-entrance={config.effects.entrance ? 'on' : 'off'}
          data-preview-mode={PREVIEW ? mode : undefined}
        >
          {!splashDone && <Splash theme={theme} ready={loaded && configReady} onDone={onSplashDone} />}

          {state.screen === 'onboard' ? (
            <Onboarding {...screenProps} />
          ) : (
            <>
              <Header
                kicker={header.kicker}
                title={header.title}
                cartCount={state.cart.length}
                unread={unreadCount(bundle, state)}
                show={{ search: hdr.search, inbox: hdr.inbox, cart: hdr.cart }}
                onMenu={navStyle === 'drawer' ? () => setDrawerOpen(true) : undefined}
                onMessages={() => dispatch({ type: 'go', screen: 'messages' })}
                showGrid={showsGridToggle(state.screen) && hdr.grid}
                onSearch={() => dispatch({ type: 'go', screen: 'search' })}
                onGrid={() => dispatch({ type: 'go', screen: state.screen === 'timeline' ? 'schedule' : 'timeline' })}
                onCart={() => dispatch({ type: 'go', screen: 'cart' })}
              />

              {navStyle === 'top' && (
                <TopTabs tabs={tabs} active={activeId} showLabels={config.navigation.showLabels} onSelect={selectTab} />
              )}

              <main
                ref={mainRef}
                className="scroll"
                style={{
                  flex: 1, overflow: 'auto',
                  // The floating dock overlays the content, so leave it room.
                  padding: `4px 20px ${navStyle === 'floating' ? 96 : 0}px`,
                }}
              >
                {state.screen === 'home' && <Home {...screenProps} />}
                {state.screen === 'page' && <CustomPage {...screenProps} />}
                {state.screen === 'schedule' && <Schedule {...screenProps} />}
                {state.screen === 'timeline' && <Timeline {...screenProps} />}
                {state.screen === 'talent' && <Talent {...screenProps} />}
                {state.screen === 'talentDetail' && <TalentDetail {...screenProps} />}
                {state.screen === 'events' && <Events {...screenProps} />}
                {state.screen === 'hall' && <Hall {...screenProps} />}
                {state.screen === 'quests' && <Quests {...screenProps} />}
                {state.screen === 'me' && <Me {...screenProps} />}
                {state.screen === 'cart' && <Cart {...screenProps} />}
                {state.screen === 'search' && <Search {...screenProps} />}
                {state.screen === 'sessionDetail' && <SessionDetail {...screenProps} />}
                {state.screen === 'journey' && <Journey {...screenProps} />}
                {state.screen === 'tickets' && <Tickets {...screenProps} />}
                {state.screen === 'shop' && <Shop {...screenProps} />}
                {state.screen === 'messages' && <Messages {...screenProps} />}
              </main>

              {(navStyle === 'bottom' || navStyle === 'floating') && (
                <TabBar
                  tabs={tabs}
                  active={activeId}
                  showLabels={config.navigation.showLabels}
                  floating={navStyle === 'floating'}
                  onSelect={selectTab}
                />
              )}

              {navStyle === 'drawer' && (
                <Drawer
                  open={drawerOpen}
                  tabs={tabs}
                  active={activeId}
                  title={theme.brand.name}
                  subtitle={bundle.event.dateRange}
                  onSelect={selectTab}
                  onClose={closeDrawer}
                />
              )}
            </>
          )}

          <BookingSheet bundle={bundle} state={state} dispatch={dispatch} />
        </div>
      </div>
    </ThemeProvider>
  );
}
